// Billing › Invoice Workbook: pick weeks, download one Excel file that
// prices the labor and equipment invoice for them by formulas over its
// backup — the reviewed time card, the HeavyJob cards (labor and
// equipment), JDE payroll (the JC600, read from JDE), per diem and fuel. The
// workbook itself is billingInvoiceWorkbookXlsx.js; this view gathers its
// sources and shows what went into the file.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import {
    loadApprovedTimeCard, loadHeavyJobCards, loadJobTimecards, loadJobTransactionDetail, apiUrl,
    loadAttendanceRoster, loadBillingSignatures, billingSignatureAction, loadBillingSignatureDocument, loadJdePayRates,
} from '../db/company';
import { mergeRateSchedule } from './billingRates';
import { equipRowAs } from './billingContract';
import { rulesFor } from './billingCardSource';
import {
    invoiceWorkbookModel, downloadInvoiceWorkbook, recentWeekEnds, weekDates, weekLabel, DEFAULT_WORKBOOK_RULES, parseCardWorkbook,
    parseJc600Workbook, jc600Index, JC600_HEADERS,
} from './billingInvoiceWorkbookXlsx';
import { invoiceApprovals } from './billingInvoiceApprovals';
import {
    invoiceFolderChecks, masterOf, masterJobsFor, weekSummaryEnvelope, weekSignature, signingPageFromTabs, signingPageFromText, buildInvoiceFolder, downloadBlob,
} from './billingInvoiceFolder';
import {
    parseInvoiceExceptions, invoiceTermsFor, otherHoursFromPay, termsRow, classRow, personRow, unitRow, deleteRow, VOLTAGRID_JOBS, EMPTY_TERMS,
} from './billingInvoiceExceptions';
import { dayBilling } from './billingWeekTracker';
import { InvoiceDrafts, parseInvoiceDrafts, newDraft, reviewDraft, draftRow, deleteDraftRow } from './billingInvoiceDrafts';
import { parseDocs, signerProgress } from './billingTicketDocs';
import { showToast } from '../helpers/toast';

const HJ_TRIES = 4;
const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// The reviewed card's own .xlsx, through the jobTimecardFile proxy (the
// browser can't call Graph itself).
const fetchCardFile = async ({ driveId, id }) => {
    const res = await fetch(`${apiUrl}api/jobTimecardFile?driveId=${encodeURIComponent(driveId)}&id=${encodeURIComponent(id)}`);
    if (!res.ok) throw new Error(`the file answered ${res.status}`);
    return res.arrayBuffer();
};

const DEFAULT_LOADERS = {
    card: loadApprovedTimeCard,
    hj: loadHeavyJobCards,
    list: loadJobTimecards,
    file: fetchCardFile,
    ledger: loadJobTransactionDetail,
};

const JC600_DATE_COLS = [4, 18]; // GL DATE, BATCH DATE

/**
 * readJobTransactionDetail's answer as an imported JC600: the same 23 values
 * a row of the export holds, dates as the Date cells ExcelJS reads.
 */
export const ledgerAsJc600 = (res) => {
    if (!res || !Array.isArray(res.rows)) throw new Error(res?.error || 'JDE sent no rows');
    const rows = res.rows.map((r) => r.map((v, i) => (JC600_DATE_COLS.includes(i) && v ? new Date(`${v}T00:00:00Z`) : v)));
    const at = res.asof ? moment(res.asof).format('M/D/YY h:mm A') : '';
    return { name: `JDE${at ? `, read ${at}` : ''}`, header: JC600_HEADERS, rows, totals: jc600Index(rows).totals, fromJde: true };
};

/**
 * Everything the workbook reads, for this job and its sister job (the crew
 * cards to both, and straight time counts across them):
 *   cards { [job]: { [we]: reviewed card | null } }
 *   hj    { [we]: { data, error? } }
 *   cardFiles { [we]: the reviewed card's .xlsx bytes }, this job only
 *   jc600 the JC600 for the job: the export imported on the page when there
 *         is one, else rebuilt live from JDE's ledger; null when JDE could
 *         not be read (the workbook's JC600 tab then waits for a paste at A1)
 * imported: { [we]: { card, bytes, name } } — time card workbooks the user
 * dropped in; each replaces SharePoint's card for its week on this job.
 * importedJc600: a JC600 export dropped in, used instead of reading JDE.
 * onStep(key, state, text) reports progress: key cards | files | hj | jc600.
 */
export const gatherWorkbookData = async ({
    job, weeks, onStep = () => {}, loaders = DEFAULT_LOADERS, wait = sleep, imported = {}, importedJc600 = null,
}) => {
    const sister = rulesFor(job).sister || '';
    const jobs = [String(job), sister].filter(Boolean);
    const sorted = [...new Set(weeks)].sort();

    onStep('cards', 'run', `Reading ${sorted.length} week${sorted.length === 1 ? '' : 's'} from SharePoint…`);
    const cards = Object.fromEntries(jobs.map((j) => [j, {}]));
    const cardErrors = [];
    for (const we of sorted) {
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(jobs.map(async (j) => {
            if (j === String(job) && imported[we]) {
                cards[j][we] = { ...imported[we].card, file: { name: imported[we].name, imported: true } };
                return;
            }
            try {
                cards[j][we] = await loaders.card({ job: j, weekEnd: we });
            } catch (e) {
                cards[j][we] = null;
                cardErrors.push(`${j} ${weekLabel(we)}`);
            }
        }));
    }
    const reviewed = sorted.filter((we) => cards[String(job)][we]?.found).length;
    onStep('cards', cardErrors.length ? 'warn' : 'done',
        `${reviewed} of ${sorted.length} week${sorted.length === 1 ? '' : 's'} reviewed${cardErrors.length ? ` · could not read ${cardErrors.join(', ')}` : ''}`);

    // The reviewed cards themselves, copied into the download tab for tab.
    // The card result names the file by its SharePoint ids; a reader
    // published before it did is matched in the Timecards folder listing.
    const cardFiles = {};
    const fileMisses = [];
    const withCard = sorted.filter((we) => cards[String(job)][we]?.found);
    let listing = null;
    if (withCard.length) onStep('files', 'run', `Fetching ${withCard.length} time card workbook${withCard.length === 1 ? '' : 's'}…`);
    for (const we of withCard) {
        if (imported[we]) { cardFiles[we] = imported[we].bytes; continue; }
        const f = cards[String(job)][we].file || {};
        let ref = f.id && f.driveId ? { id: f.id, driveId: f.driveId } : null;
        if (!ref) {
            if (!listing) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    listing = (await loaders.list({ job: String(job) })) || { files: [] };
                } catch (e) {
                    listing = { files: [] };
                }
            }
            const files = listing.files || [];
            const hit = (f.webUrl && files.find((x) => x.webUrl === f.webUrl))
                || files.find((x) => x.name === f.name && (!f.lastModified || x.lastModified === f.lastModified));
            if (hit) ref = { id: hit.id, driveId: hit.driveId };
        }
        if (!ref) { fileMisses.push(weekLabel(we)); continue; }
        try {
            // eslint-disable-next-line no-await-in-loop
            cardFiles[we] = await loaders.file(ref);
        } catch (e) {
            fileMisses.push(weekLabel(we));
        }
    }
    if (withCard.length) {
        onStep('files', fileMisses.length ? 'warn' : 'done', `${withCard.length - fileMisses.length} of ${withCard.length} copied in${fileMisses.length ? ` · not fetched: ${fileMisses.join(', ')}` : ''}`);
    } else onStep('files', 'done', 'no reviewed cards to copy in');

    // One job-week a call: a VoltaGrid week is a hundred-odd cards, and
    // HCSS rate-limits the API ("Rate limit is exceeded. Try again in 22
    // seconds.") — wait what it asks and try again.
    const hj = {};
    let hjRows = 0;
    const readCards = async (body, label) => {
        let last = '';
        let wasLimited = false;
        for (let attempt = 1; attempt <= HJ_TRIES; attempt += 1) {
            let res;
            try {
                // eslint-disable-next-line no-await-in-loop
                res = await loaders.hj(body);
            } catch (e) {
                res = { error: e.message };
            }
            if (res && !res.error && res.configured !== false) return { data: res.data || [] };
            last = !res ? 'HeavyJob did not answer' : res.configured === false ? 'HeavyJob is not set up on this service' : String(res.error);
            const limited = /429|rate limit/i.test(last);
            // A function instance that was rate-limited while it looked the
            // job up answers "HeavyJob has no job" for the next minute
            // (hcssShared findJob) — after a 429 that means "not yet".
            const coldMiss = wasLimited && /has no job/i.test(last);
            wasLimited = wasLimited || limited;
            if (attempt === HJ_TRIES || !(limited || coldMiss || !res)) break;
            const secs = coldMiss ? 62 : limited ? Number(/try again in (\d+) second/i.exec(last)?.[1] || 20) + 2 : 5;
            onStep('hj', 'run', `${label}: HeavyJob is busy, trying again in ${secs} s…`);
            // eslint-disable-next-line no-await-in-loop
            await wait(secs * 1000);
        }
        return { data: [], error: wasLimited ? 'HeavyJob rate limit — try again in a minute' : last };
    };
    for (let i = 0; i < sorted.length; i += 1) {
        const we = sorted[i];
        const d = weekDates(we);
        const data = [];
        let error = '';
        let sisterError = '';
        for (const j of jobs) {
            const label = `${weekLabel(we)} (${i + 1} of ${sorted.length}), ${j}`;
            onStep('hj', 'run', `Reading ${label}…`);
            // eslint-disable-next-line no-await-in-loop
            const r = await readCards({ job: j, start: d[0], end: d[6] }, label);
            // The sister job only feeds straight time; this job's cards are the backup.
            if (r.error && j === String(job)) error = r.error;
            else if (r.error) sisterError = r.error;
            data.push(...r.data);
        }
        hj[we] = { data, ...(error ? { error } : {}), ...(sisterError ? { sisterError } : {}) };
        hjRows += data.length;
    }
    const hjFailed = sorted.filter((we) => hj[we].error);
    onStep('hj', hjFailed.length ? 'warn' : 'done', hjFailed.length
        ? `not read for ${hjFailed.map(weekLabel).join(', ')}`
        : `${hjRows.toLocaleString()} card lines`);

    let jc600 = importedJc600;
    if (!jc600) {
        onStep('jc600', 'run', `Reading ${job}'s job ledger from JDE…`);
        try {
            jc600 = ledgerAsJc600(await loaders.ledger({ job: String(job) }));
        } catch (e) {
            onStep('jc600', 'warn', `could not read JDE (${e.message}) — import the JC600 export, or paste it into the workbook's JC600 tab at A1`);
        }
    }
    if (jc600) {
        onStep('jc600', 'done', `${jc600.name} · ${jc600.totals.lines.toLocaleString()} payroll lines · latest ${jc600.totals.latest ? weekLabel(jc600.totals.latest) : 'none'}`);
    }
    return { cards, cardFiles, hj, sister, jc600 };
};

/**
 * The weeks already invoiced in DocuSign: { [weekEnd]: [{ no, status }] } for
 * each labor and equipment invoice approval that went out (not a draft) and
 * was not voided, over the week's Monday to Sunday. An invoice that bills only
 * third party or the mobilization lump sum is not this workbook's invoice, so
 * it leaves its weeks unmarked; one whose cover has not been read yet counts.
 */
export const invoicedWeeks = (envelopes, weeks) => {
    const sent = invoiceApprovals(envelopes).rows.filter((r) => r.status !== 'Draft' && r.from && r.to
        && (!r.kinds.length || r.kinds.includes('Labor') || r.kinds.includes('Equipment')));
    const out = {};
    (weeks || []).forEach((we) => {
        const from = weekDates(we)[0];
        const on = sent.filter((r) => r.from <= we && r.to >= from).map((r) => ({ no: r.no, status: r.status }));
        if (on.length) out[we] = on;
    });
    return out;
};

/**
 * Where each week's summary stands — the week summary the client signs on
 * Daily Field Tickets, read off the same envelopes and ticket documents as
 * that tab's summary strip (billingWeekTracker dayBilling):
 * { [weekEnd]: [{ status, from, to, at, waitOn, signed, total, env }] },
 * one entry per summary envelope over the week's days (a Day and a Night
 * summary make two), newest signed first. `status` is Signed, Out or
 * Declined; `at` is when it was signed, else when it went out; `waitOn`
 * names who still has it. A week with no summary out is absent.
 */
export const summaryWeeks = (envelopes, userRows, weeks) => {
    const at = dayBilling({ envelopes, docs: parseDocs(userRows) });
    const out = {};
    (weeks || []).forEach((we) => {
        const seen = {};
        weekDates(we).forEach((d) => {
            const s = at(d).summary;
            if (s && !seen[s.env.RowID]) seen[s.env.RowID] = s;
        });
        const list = Object.values(seen).map((s) => {
            const st = String(s.env.Status || '').toLowerCase();
            const p = signerProgress(s.env);
            return {
                status: st === 'completed' ? 'Signed' : st === 'declined' ? 'Declined' : 'Out',
                from: s.from,
                to: s.to,
                at: st === 'completed' ? s.env.Completed_At_Utc : st === 'declined' ? s.env.Declined_At_Utc : s.env.Sent_At_Utc,
                waitOn: ['sent', 'delivered'].includes(st) ? p.waiting.map((r) => r.name) : [],
                signed: p.signed,
                total: p.total,
                env: s.env,
            };
        });
        if (list.length) out[we] = list;
    });
    return out;
};

// ── View ──

const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const hours = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const STEPS = [
    { key: 'cards', label: 'Reviewed time cards' },
    { key: 'files', label: 'Time card workbooks' },
    { key: 'hj', label: 'HeavyJob cards' },
    { key: 'jc600', label: 'JDE payroll (JC600)' },
    { key: 'pay', label: 'JDE payroll (other jobs)' },
    { key: 'master', label: 'Attendance & Force Report master' },
    { key: 'signed', label: 'Signed week summaries' },
    { key: 'build', label: 'Excel workbook' },
    { key: 'folder', label: 'Invoice folder' },
    { key: 'draft', label: 'Invoice draft' },
];

// Names compared the way the model does: case, spaces and punctuation aside (a saved "Man lift - 135 Lift" is the sheet's "Man lift - 135' Lift").
const sameName = (a, b) => String(a || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() === String(b || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const b64bytes = (b64) => { const bin = atob(String(b64 || '')); const out = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i); return out; };
const exInput = { height: 32, padding: '0 10px', borderRadius: 'var(--radius)', border: '1px solid var(--input)', background: 'var(--background)', color: 'var(--foreground)', fontFamily: 'inherit', fontSize: 13 };
const exRow = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' };
const ExGroup = ({ label, children }) => (
    <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', letterSpacing: '0.04em', marginBottom: 6 }}>{label}</div>
        {children}
    </div>
);
const ExField = ({ label, children }) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: 'var(--text-secondary)' }}>
        {label}
        {children}
    </label>
);
const ExRemove = ({ onClick, disabled }) => (
    <button type="button" onClick={onClick} disabled={disabled} title="Remove" aria-label="Remove" style={{ width: 28, height: 28, borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--background)', color: 'var(--text-secondary)', cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>×</button>
);
const Check = ({ label, checked, onChange, disabled, title }) => (
    <label title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--foreground)', cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} style={{ accentColor: 'var(--primary)' }} />
        {label}
    </label>
);

const cardStyle = {
    background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-xs)',
    padding: '16px 20px', marginBottom: 12,
};
const th = {
    fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', textAlign: 'left', background: 'var(--subtle)',
    borderBottom: '1px solid var(--border)', padding: '8px 12px', whiteSpace: 'nowrap',
};
const td = { fontSize: 14, padding: '8px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', color: 'var(--foreground)' };
const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

const Badge = ({ tone, children }) => {
    const t = tone === 'warn' ? { bg: 'var(--warning-tint)', fg: 'var(--warning-text)' }
        : tone === 'bad' ? { bg: 'var(--destructive-tint)', fg: 'var(--destructive-text)' }
            : { bg: 'var(--muted)', fg: 'var(--text-secondary)' };
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 500, background: t.bg, color: t.fg }}>
            {children}
        </span>
    );
};

const GhostButton = ({ label, onClick, disabled }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        style={{ height: 32, padding: '0 10px', border: '1px solid transparent', background: 'transparent', boxShadow: 'none', color: 'var(--primary)', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
        {label}
    </button>
);

const OutlineButton = ({ label, onClick, running }) => (
    <button
        type="button"
        disabled={running}
        onClick={onClick}
        style={{
            height: 32, padding: '0 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontFamily: 'inherit',
            background: 'var(--background)', color: 'var(--foreground)', boxShadow: 'var(--shadow-xs)', fontSize: 14, fontWeight: 500,
            cursor: running ? 'wait' : 'pointer',
        }}
    >
        {label}
    </button>
);

const StepRow = ({ label, step }) => {
    const state = step?.state || 'wait';
    const mark = state === 'done' ? '✓' : state === 'warn' ? '!' : state === 'run' ? '…' : '·';
    const color = state === 'done' ? 'var(--success)' : state === 'warn' ? 'var(--warning)' : 'var(--muted-foreground)';
    return (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 0', fontSize: 13 }}>
            <span style={{ width: 14, textAlign: 'center', fontWeight: 600, color }}>{mark}</span>
            <span style={{ width: 160, color: 'var(--foreground)', fontWeight: 500 }}>{label}</span>
            <span style={{ color: state === 'warn' ? 'var(--warning-text)' : 'var(--text-secondary)' }}>{step?.text || (state === 'wait' ? 'waiting' : '')}</span>
        </div>
    );
};

export default function BillingInvoiceWorkbook({
    jobKey, jobName, client, userName, userRows, settings, equipName, txns, today, envelopes,
    saveRows = null, versionLocked = false,
    prefill = null,          // { weeks: [weekEnd], build, nonce } — weeks picked elsewhere (Labor & Equipment's Create invoice); build: run the folder at once
    onPrefillUsed = null,
}) {
    const now = today || moment().format('YYYY-MM-DD');
    const [shown, setShown] = useState(12);
    const allWeeks = useMemo(() => recentWeekEnds(now, shown), [now, shown]);
    const thisWeek = allWeeks[0];
    const invoiced = useMemo(() => invoicedWeeks(envelopes, allWeeks), [envelopes, allWeeks]);
    const summaries = useMemo(() => summaryWeeks(envelopes, userRows, allWeeks), [envelopes, userRows, allWeeks]);
    // Default: the last full week.
    const [picked, setPicked] = useState(() => new Set([recentWeekEnds(now, 2)[1]]));
    const [steps, setSteps] = useState({});
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [hover, setHover] = useState(false);
    // Time card workbooks dropped in: { [we]: { card, bytes, name } }.
    const [imported, setImported] = useState({});
    const [importMsg, setImportMsg] = useState('');
    const fileInput = useRef(null);
    // A JC600 export dropped in: { name, header, rows, totals } — written
    // into the JC600 tab at A1 exactly as a paste would put it. Without one
    // the download reads the same rows from JDE.
    const [jc600, setJc600] = useState(null);
    const [jcMsg, setJcMsg] = useState('');
    const jcInput = useRef(null);
    const sister = rulesFor(jobKey).sister || '';
    // Build invoice folder: the same gather, then the checks, then the zip.
    const [folder, setFolder] = useState(null); // { checks, built: { name, files, packagePages } | null }
    const [acceptMaster, setAcceptMaster] = useState(false);
    const [allowUnsigned, setAllowUnsigned] = useState(false);
    const [folderHover, setFolderHover] = useState(false);
    // The job's invoice terms and exception tables (billingInvoiceExceptions.js), and their editor's draft.
    const exceptions = useMemo(() => parseInvoiceExceptions(userRows), [userRows]);
    const [exDraft, setExDraft] = useState(null); // { terms, classes, people, units, removed: [rowId] } while editing
    const [exOpen, setExOpen] = useState(false);
    const ex = exDraft || exceptions;
    // The job's invoice drafts (billingInvoiceDrafts.js): one per folder built, with its reviews.
    const drafts = useMemo(() => parseInvoiceDrafts(userRows), [userRows]);
    const draftInvoiced = useMemo(() => invoicedWeeks(envelopes, [...new Set([...allWeeks, ...drafts.flatMap((d) => d.weeks)])]), [envelopes, allWeeks, drafts]);
    // Imports, drafts and results belong to the job they were made on: a job switch clears them
    // (11/2: a 71 time card imported for WE 10/18 priced 77's build after the header switched jobs).
    useEffect(() => { setImported({}); setJc600(null); setImportMsg(''); setJcMsg(''); setResult(null); setFolder(null); setSteps({}); setExDraft(null); }, [jobKey]);

    const schedule = useMemo(() => mergeRateSchedule(userRows), [userRows]);
    const fuelTxns = useMemo(() => (txns || []).filter((t) => String(t.ct) === '52300'), [txns]);

    const toggle = (we) => setPicked((p) => {
        const n = new Set(p);
        if (n.has(we)) n.delete(we); else n.add(we);
        return n;
    });
    const pickLast = (k) => setPicked(new Set(recentWeekEnds(now, k + 1).slice(1)));

    const importCards = async (fileList) => {
        const files = [...(fileList || [])];
        if (!files.length) return;
        const ExcelJS = (await import('exceljs')).default;
        const added = {};
        const msgs = [];
        for (const f of files) {
            try {
                // eslint-disable-next-line no-await-in-loop
                const bytes = await f.arrayBuffer();
                const wb = new ExcelJS.Workbook();
                // eslint-disable-next-line no-await-in-loop
                await wb.xlsx.load(bytes);
                const card = parseCardWorkbook(wb, now);
                if (!card.found) { msgs.push(`${f.name}: not a time card workbook (${card.reason}).`); continue; }
                if (card.job && card.job !== String(jobKey)) { msgs.push(`${f.name}: that card is job ${card.job}, not ${jobKey}.`); continue; }
                added[card.weekEnd] = { card, bytes, name: f.name };
            } catch (e) {
                msgs.push(`${f.name}: could not be read (${e.message}).`);
            }
        }
        if (Object.keys(added).length) {
            setImported((x) => ({ ...x, ...added }));
            setPicked((p) => new Set([...p, ...Object.keys(added)]));
            // An older week than the chips show: show enough of them.
            const oldest = Object.keys(added).sort()[0];
            const back = Math.ceil(moment(thisWeek, 'YYYY-MM-DD').diff(moment(oldest, 'YYYY-MM-DD'), 'days') / 7) + 1;
            if (back > shown) setShown(Math.min(52, back));
        }
        setImportMsg(msgs.join(' '));
    };

    const importJc600 = async (file) => {
        if (!file) return;
        setJcMsg('');
        try {
            const ExcelJS = (await import('exceljs')).default;
            const wb = new ExcelJS.Workbook();
            await wb.xlsx.load(await file.arrayBuffer());
            const x = parseJc600Workbook(wb);
            if (!x.found) { setJcMsg(`${file.name}: not a JC600 export — ${x.reason}.`); return; }
            // The formulas do not read the JOB column: one job's export only.
            if (x.jobs.length !== 1 || x.jobs[0] !== String(jobKey)) {
                setJcMsg(`${file.name}: that export is job ${x.jobs.join(', ') || '(none)'}, not ${jobKey}. Export ${jobKey} alone.`);
                return;
            }
            setJc600({ name: file.name, header: x.header, rows: x.rows, totals: jc600Index(x.rows).totals });
        } catch (e) {
            setJcMsg(`${file.name}: could not be read (${e.message}).`);
        }
    };

    const run = async () => {
        const weeks = [...picked].sort();
        if (!weeks.length || running) return;
        setRunning(true);
        setResult(null);
        setFolder(null);
        setSteps({});
        const onStep = (key, state, text) => setSteps((s) => ({ ...s, [key]: { state, text } }));
        try {
            const data = await gatherWorkbookData({ job: jobKey, weeks, onStep, imported, importedJc600: jc600 });
            const otherHours = await gatherOtherHours(weeks, onStep);
            const master = await gatherMaster(onStep);
            onStep('build', 'run', 'Writing the formulas…');
            const model = buildModel(weeks, data, otherHours, master);
            const out = await downloadInvoiceWorkbook(model, { cardFiles: data.cardFiles });
            onStep('build', 'done', out.name);
            setResult({ model, fileName: out.name, notes: out.notes || [], cardTabs: out.cardTabs || [] });
        } catch (e) {
            onStep('build', 'warn', `failed: ${e.message}`);
            showToast(`The invoice workbook could not be built: ${e.message}`, 'error');
        } finally {
            setRunning(false);
        }
    };

    // Hours on the other VoltaGrid jobs from JDE payroll, so straight time counts every job a
    // person worked that week (Seth 10/28). A pull that fails leaves the sister card's hours only.
    const gatherOtherHours = async (weeks, onStep) => {
        const from = weekDates(weeks[0])[0];
        const to = weeks[weeks.length - 1];
        const jobs = VOLTAGRID_JOBS.includes(String(jobKey)) ? VOLTAGRID_JOBS : [String(jobKey), ...VOLTAGRID_JOBS].slice(0, 6);
        onStep('pay', 'run', `Reading payroll hours on ${jobs.filter((j) => j !== String(jobKey)).join(', ')}…`);
        try {
            const r = await loadJdePayRates({ from, to, jobs });
            if (!r || r.error) throw new Error(r?.error || 'no answer');
            const other = otherHoursFromPay(r.rows || [], jobKey, weeks);
            const people = new Set(Object.values(other).flatMap((w) => Object.keys(w))).size;
            onStep('pay', 'done', people ? `${people} of this job's people have payroll hours on another job` : 'no payroll hours on another job for these weeks');
            return other;
        } catch (e) {
            onStep('pay', 'warn', `not read (${e.message}) — straight time counts only this job's card${sister ? ` and ${sister}'s` : ''}`);
            return null;
        }
    };
    // The Attendance & Force Report masters (70's carries the billing classifications, then the
    // job's own, then 71's): each person bills under the master's rate sheet line (Seth 11/2).
    const gatherMaster = async (onStep) => {
        const masterJobs = masterJobsFor(jobKey);
        onStep('master', 'run', `Reading ${masterJobs.join(', ')}…`);
        const rosters = await Promise.all(masterJobs.map(async (j) => ({ job: j, res: await loadAttendanceRoster({ job: j }).catch(() => ({ found: false })) })));
        const master = masterOf(rosters);
        onStep('master', master.found.length ? 'done' : 'warn', master.found.length ? `${Object.keys(master.people).length} people on ${master.found.join(', ')}` : 'no Attendance & Force Report master was read');
        return master;
    };
    const buildModel = (weeks, data, otherHours = null, master = null) => invoiceWorkbookModel({
        job: jobKey, jobName, client, weeks,
        cards: data.cards, hj: data.hj, jc600: data.jc600, otherHours,
        laborRows: schedule.labor, masterClasses: master ? Object.fromEntries(Object.entries(master.people).map(([id, p]) => [id, p.billCls])) : null,
        classRules: exceptions.classes, unitRules: exceptions.units,
        cfg: schedule.cfg,
        rules: { ...DEFAULT_WORKBOOK_RULES, pdRate: Number(settings?.pd?.rate) || DEFAULT_WORKBOOK_RULES.pdRate },
        equipRows: schedule.equip, fuelRows: schedule.fuel, fuelSettings: settings?.fuel,
        namer: equipName, equipAs: equipRowAs(jobKey),
        fuelTxns, preparedBy: userName || '', preparedAt: moment().format('YYYY-MM-DD HH:mm'),
    });

    // The invoice folder: the workbook's model, the masters, the signed week summaries,
    // the checks, and only then the zip (billingInvoiceFolder.js).
    const runFolder = async (weeksIn = null) => {
        const weeks = Array.isArray(weeksIn) ? [...new Set(weeksIn)].sort() : [...picked].sort();
        if (!weeks.length || running) return;
        setRunning(true);
        setResult(null);
        setFolder(null);
        setSteps({});
        const onStep = (key, state, text) => setSteps((s) => ({ ...s, [key]: { state, text } }));
        const options = { acceptMaster, allowUnsigned };
        try {
            const data = await gatherWorkbookData({ job: jobKey, weeks, onStep, imported, importedJc600: jc600 });
            const otherHours = await gatherOtherHours(weeks, onStep);
            const master = await gatherMaster(onStep);
            const model = buildModel(weeks, data, otherHours, master);

            onStep('signed', 'run', 'Reading the week summary envelopes…');
            const sig = await loadBillingSignatures({ job: jobKey }).catch((e) => ({ error: e.message }));
            const envelopes = sig?.envelopes || [];
            const weekSigs = {};
            const signedWeeks = [];
            for (const we of weeks) {
                const env = weekSummaryEnvelope(envelopes, jobKey, we);
                const ws = weekSignature(env);
                weekSigs[we] = ws;
                if (!env) continue;
                onStep('signed', 'run', `${weekLabel(we)}: fetching the ${ws.complete ? 'signed' : 'sent'} week summary from DocuSign…`);
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const [tabs, doc] = await Promise.all([
                        billingSignatureAction({ job: jobKey, rowId: env.RowID, action: 'tabs' }).catch(() => null),
                        loadBillingSignatureDocument({ job: jobKey, rowId: env.RowID }),
                    ]);
                    if (!doc || doc.error || !doc.contentBase64) throw new Error(doc?.error || 'no document came back');
                    const bytes = b64bytes(doc.contentBase64);
                    ws.pages = signingPageFromTabs(tabs?.recipients);
                    if (!ws.pages) {
                        // DocuSign kept no tabs on this envelope: find the signing page in the PDF's text.
                        onStep('signed', 'run', `${weekLabel(we)}: no tabs on the envelope — reading the summary's signing page from the PDF…`);
                        ws.pages = await signingPageFromText(bytes).catch(() => null); // eslint-disable-line no-await-in-loop
                        ws.pagesFrom = ws.pages ? 'text' : '';
                    } else ws.pagesFrom = 'tabs';
                    signedWeeks.push({ we, bytes, pages: ws.pages, complete: ws.complete, signed: ws.signed, signers: ws.signers, outstanding: ws.outstanding });
                } catch (e) {
                    ws.complete = false;
                    ws.why = `the DocuSign copy could not be read (${e.message})`;
                }
            }
            const done = weeks.filter((we) => weekSigs[we]?.complete).length;
            onStep('signed', done === weeks.length ? 'done' : 'warn', `${done} of ${weeks.length} week${weeks.length === 1 ? '' : 's'} signed by every customer representative`);

            const invoice = invoiceTermsFor(exceptions);
            const checks = invoiceFolderChecks({ model, master, laborSheet: schedule.labor, weekSigs, invoice, exceptions, options });
            // Open conditions no longer stop the download (Seth 11/4: "it should still let me download" /
            // "the invoice workbook not excel"): the folder builds, named NOT READY, with every
            // condition as a BLOCK line on the internal review and in the preflight file. It is not
            // recorded as a draft to validate until the conditions are settled.
            const open = checks.blocks.length;
            onStep('build', 'run', 'Writing the summary, review, package, labor analysis and workbook…');
            const built = await buildInvoiceFolder({
                model, jobName, client, invoice, checks, master, weekSigs, options, exceptions, cardFiles: data.cardFiles,
                signedWeeks: signedWeeks.filter((w) => weeks.includes(w.we)), preparedBy: userName || '',
            });
            if (open) built.name = `NOT READY - ${built.name}`;
            onStep('build', 'done', 'in the zip');
            downloadBlob(built.blob, built.name);
            onStep('folder', open ? 'warn' : 'done', open ? `${built.name} — ${open} condition${open === 1 ? '' : 's'} still open, listed on the review` : built.name);
            setFolder({ checks, built, model });
            setResult({ model, fileName: built.name, notes: built.notes || [], cardTabs: built.cardTabs || [] });
            if (open) {
                onStep('draft', 'warn', 'not recorded — settle the conditions and build again to record a draft');
            } else if (saveRows && !versionLocked) {
                // Record the draft so someone else can validate it (Invoice drafts, below).
                saveRows([draftRow(newDraft({ weeks, model, checks, built, options, by: userName }))]);
                onStep('draft', 'done', 'recorded under Invoice drafts for someone else to validate');
            }
        } catch (e) {
            onStep('folder', 'warn', `failed: ${e.message}`);
            showToast(`The invoice folder could not be built: ${e.message}`, 'error');
        } finally {
            setRunning(false);
        }
    };

    // Pick a draft's weeks and build again; validate or send back; remove.
    const showWeeks = (weeks) => {
        const back = Math.ceil(moment(thisWeek, 'YYYY-MM-DD').diff(moment(weeks[0], 'YYYY-MM-DD'), 'days') / 7) + 1;
        if (back > shown) setShown(Math.min(52, back));
    };
    const rebuildDraft = (d) => { setPicked(new Set(d.weeks)); showWeeks(d.weeks); runFolder(d.weeks); };
    const reviewOne = (d, { status, note }) => {
        if (!saveRows || versionLocked) return;
        saveRows([draftRow(reviewDraft(d, { by: userName, status, note }))]);
        showToast(status === 'validated' ? 'Draft validated' : 'Draft sent back for changes', 'success');
    };
    const removeDraft = (d) => { if (saveRows && !versionLocked) saveRows([deleteDraftRow(d)]); };
    // Weeks handed over from another tab: pick them, and build the folder when asked to.
    const prefillNonce = prefill?.nonce || 0;
    useEffect(() => {
        if (!prefillNonce || !Array.isArray(prefill?.weeks) || !prefill.weeks.length) return;
        const weeks = [...new Set(prefill.weeks)].sort();
        setPicked(new Set(weeks));
        showWeeks(weeks);
        if (onPrefillUsed) onPrefillUsed();
        if (prefill.build) runFolder(weeks);
    }, [prefillNonce]);

    // The exception editor: a draft of the four tables, saved as billing rows in one go.
    const editEx = (patch) => setExDraft({ ...(exDraft || { ...exceptions, removed: [] }), ...patch });
    const editList = (key, i, patch) => editEx({ [key]: ex[key].map((r, j) => (j === i ? { ...r, ...patch } : r)) });
    const addTo = (key, row) => editEx({ [key]: [...ex[key], row] });
    const removeFrom = (key, i) => { const r = ex[key][i]; editEx({ [key]: ex[key].filter((_, j) => j !== i), removed: [...(exDraft?.removed || []), ...(r.rowId ? [r.rowId] : [])] }); };
    const saveExceptions = () => {
        if (!exDraft || !saveRows || versionLocked) return;
        const rows = [
            termsRow(exDraft.terms),
            ...exDraft.classes.filter((r) => String(r.card || '').trim()).map(classRow),
            ...exDraft.people.filter((r) => String(r.id || '').trim()).map(personRow),
            ...exDraft.units.filter((r) => String(r.match || '').trim()).map(unitRow),
            ...(exDraft.removed || []).filter((id) => id !== 'invoice_terms').map(deleteRow),
        ];
        saveRows(rows);
        setExDraft(null);
        showToast('Invoice terms and exceptions saved', 'success');
    };
    const laborLines = (schedule.labor || []).map((r) => r.name);
    const equipItems = [...new Set((schedule.equip || []).map((r) => r.name))];

    const weeksSorted = [...picked].sort();
    const m = result?.model;

    return (
        <div style={{ maxWidth: 1180 }}>
            <div style={cardStyle}>
                <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--foreground)' }}>Invoice workbook</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, maxWidth: 820, lineHeight: 1.5 }}>
                    One Excel file for the weeks you pick: the labor and equipment invoice, worked out by formulas over its backup.
                    Labor is billed at each person&apos;s JDE wage from the JC600 export × {schedule.cfg.m}, overtime × {schedule.cfg.otf} over 40 hours a week
                    {sister ? ` across ${jobKey} and ${sister}` : ''}. Hours, per diem and equipment come from the reviewed time card,
                    or from HeavyJob for a week that has no reviewed card yet.
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 8 }}>
                    Tabs: Summary · Labor Invoice · Per Diem · Equipment Invoice · Fuel · Time Card (labor, equipment) · each week&apos;s reviewed time card as it is · HeavyJob (labor, equipment) · JC600 (JDE&apos;s export, as it is: paste a new one there at A1) · JDE Labor Cost (what it reads off it) · Shifts
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 16, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', marginRight: 8 }}>WEEKS</span>
                    <GhostButton label="Last week" onClick={() => pickLast(1)} disabled={running} />
                    <GhostButton label="Last 2 weeks" onClick={() => pickLast(2)} disabled={running} />
                    <GhostButton label="Clear" onClick={() => setPicked(new Set())} disabled={running || !picked.size} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginBottom: 8 }}>
                    Each week shows where its week summary stands with the client (Daily Field Tickets). The check marks a week on a labor or equipment invoice approval in DocuSign; a third-party or mobilization invoice does not mark one.
                </div>
                <div role="group" aria-label="Weeks" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {allWeeks.map((we) => {
                        const on = picked.has(we);
                        const d = weekDates(we);
                        const inv = invoiced[we];
                        const invText = inv ? `Invoiced: ${inv.map((x) => `${x.no} (${x.status === 'Out' ? 'out for approval' : x.status.toLowerCase()})`).join(', ')}`
                            : we === thisWeek ? '' : 'Not on a labor or equipment invoice approval in DocuSign';
                        // The week summary, as the Daily Field Tickets strip reads it.
                        const sums = summaries[we] || [];
                        const md = (t) => (t ? moment.utc(t).local().format('M/D') : '');
                        const sumLine = sums.length ? sums.map((s) => (s.status === 'Signed' ? `Signed ${md(s.at)}`
                            : s.status === 'Declined' ? 'Declined'
                                : s.waitOn.length ? `Out · ${s.waitOn.length === 1 ? s.waitOn[0].split(' ')[0] : `${s.signed} of ${s.total}`}`
                                    : s.total ? `Out · ${s.signed} of ${s.total}` : `Out ${md(s.at)}`)).join(' / ')
                            : we === thisWeek ? '' : 'No summary';
                        const sumTone = !sums.length ? 'none' : sums.some((s) => s.status === 'Declined') ? 'bad'
                            : sums.every((s) => s.status === 'Signed') ? 'good' : 'out';
                        const sumText = sums.length ? sums.map((s) => [
                            `Week summary ${moment(s.from, 'YYYY-MM-DD').format('M/D')}–${moment(s.to, 'YYYY-MM-DD').format('M/D')}: ${s.status.toLowerCase()}${s.status === 'Signed' ? ` ${md(s.at)}` : ''}`,
                            s.waitOn.length ? ` · waiting on ${s.waitOn.join(', ')}` : s.total && s.status !== 'Signed' ? ` · ${s.signed} of ${s.total} signed` : '',
                            ` · sent ${md(s.env.Sent_At_Utc)}${s.env.Sent_By ? ` by ${s.env.Sent_By}` : ''}`,
                        ].join('')).join('\n')
                            : we === thisWeek ? '' : 'No week summary has gone out over these days';
                        const titleText = [sumText, invText].filter(Boolean).join('\n');
                        return (
                            <button
                                key={we}
                                type="button"
                                aria-pressed={on}
                                aria-label={titleText ? `${weekLabel(we)}, ${titleText.replace(/\n/g, '; ')}` : undefined}
                                title={titleText || undefined}
                                onClick={() => toggle(we)}
                                disabled={running}
                                style={{
                                    position: 'relative',
                                    width: 118, padding: '6px 10px', textAlign: 'left', borderRadius: 'var(--radius)', fontFamily: 'inherit',
                                    border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                                    background: on ? 'var(--primary)' : 'var(--background)',
                                    color: on ? 'var(--primary-foreground)' : 'var(--foreground)',
                                    boxShadow: 'var(--shadow-xs)', cursor: running ? 'wait' : 'pointer',
                                }}
                            >
                                {inv ? (
                                    // Invoiced in DocuSign: a small green check in the corner; the title says which invoice.
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            position: 'absolute', top: 5, right: 5, width: 16, height: 16, borderRadius: 'var(--radius-pill)',
                                            background: 'var(--success-tint)', color: 'var(--success)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        }}
                                    >
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 6 9 17l-5-5" />
                                        </svg>
                                    </span>
                                ) : null}
                                <div style={{ fontSize: 13, fontWeight: 600 }}>{weekLabel(we)}</div>
                                <div style={{ fontSize: 12, fontWeight: 400, opacity: on ? 0.85 : 1, color: on ? 'inherit' : 'var(--text-secondary)' }}>
                                    {we === thisWeek ? 'in progress' : `${moment(d[0]).format('M/D')} – ${moment(d[6]).format('M/D')}`}
                                </div>
                                {sumLine ? (
                                    // The week summary: a status dot and its word, as a table cell draws a status.
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: on ? 0.9 : 1, color: on ? 'inherit' : sumTone === 'good' ? 'var(--success-text)' : sumTone === 'bad' ? 'var(--destructive-text)' : sumTone === 'out' ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                                        <span
                                            aria-hidden="true"
                                            style={{
                                                flex: 'none', width: 7, height: 7, borderRadius: 'var(--radius-pill)',
                                                background: sumTone === 'good' ? 'var(--success)' : sumTone === 'bad' ? 'var(--destructive)' : sumTone === 'out' ? (on ? 'var(--primary-foreground)' : 'var(--primary)') : 'transparent',
                                                border: sumTone === 'none' ? `1px solid ${on ? 'var(--primary-foreground)' : 'var(--border-strong)'}` : 'none',
                                            }}
                                        />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{sumLine}</span>
                                    </div>
                                ) : null}
                            </button>
                        );
                    })}
                    {shown < 52 ? (
                        <GhostButton label="Older weeks" onClick={() => setShown((n) => n + 12)} disabled={running} />
                    ) : null}
                </div>

                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', width: 52 }}>IMPORT</span>
                        <OutlineButton label="Time card workbook (.xlsx)…" running={running} onClick={() => fileInput.current && fileInput.current.click()} />
                        <input
                            ref={fileInput}
                            type="file"
                            accept=".xlsx"
                            multiple
                            hidden
                            onChange={(e) => { importCards(e.target.files); e.target.value = ''; }}
                        />
                        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                            Read from SharePoint when you download. Import a local copy only to use it instead for its week.
                        </span>
                    </div>
                    {Object.keys(imported).length ? (
                        <div style={{ marginTop: 8 }}>
                            {Object.entries(imported).sort().map(([we, x]) => (
                                <div key={we} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--foreground)', padding: '2px 0' }}>
                                    <span style={{ fontWeight: 500 }}>{weekLabel(we)}</span>
                                    <span style={{ color: 'var(--text-secondary)' }}>
                                        {x.name} · {x.card.labor.length} people · {x.card.equipment.length} units
                                    </span>
                                    <GhostButton
                                        label="Remove"
                                        disabled={running}
                                        onClick={() => setImported((cur) => { const n = { ...cur }; delete n[we]; return n; })}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : null}
                    {importMsg ? <div style={{ marginTop: 6, fontSize: 13, color: 'var(--warning-text)' }}>{importMsg}</div> : null}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
                        <span style={{ width: 52 }} />
                        <OutlineButton label="JC600 export (.xlsx)…" running={running} onClick={() => jcInput.current && jcInput.current.click()} />
                        <input
                            ref={jcInput}
                            type="file"
                            accept=".xlsx"
                            hidden
                            onChange={(e) => { importJc600(e.target.files && e.target.files[0]); e.target.value = ''; }}
                        />
                        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                            JDE&apos;s Job Transaction Detail for {jobKey} is read from JDE when you download. Import an export only to use it instead.
                        </span>
                    </div>
                    {jc600 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--foreground)', padding: '2px 0', marginTop: 8 }}>
                            <span style={{ fontWeight: 500 }}>JC600</span>
                            <span style={{ color: 'var(--text-secondary)' }}>
                                {jc600.name} · {jc600.totals.lines.toLocaleString()} payroll lines · {hours(jc600.totals.hrs)} hrs · latest {jc600.totals.latest ? weekLabel(jc600.totals.latest) : 'none'}
                            </span>
                            <GhostButton label="Remove" disabled={running} onClick={() => setJc600(null)} />
                        </div>
                    ) : null}
                    {jcMsg ? <div style={{ marginTop: 6, fontSize: 13, color: 'var(--warning-text)' }}>{jcMsg}</div> : null}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        onClick={run}
                        disabled={running || !picked.size}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                        style={{
                            height: 36, padding: '0 16px', borderRadius: 'var(--radius)', border: '1px solid transparent', fontFamily: 'inherit',
                            background: hover && !running && picked.size ? 'var(--primary-hover)' : 'var(--primary)', color: 'var(--primary-foreground)',
                            fontSize: 14, fontWeight: 500, cursor: running ? 'wait' : picked.size ? 'pointer' : 'not-allowed', opacity: picked.size ? 1 : 0.5,
                        }}
                    >
                        {running ? 'Building…' : 'Download Excel'}
                    </button>
                    <button
                        type="button"
                        onClick={runFolder}
                        disabled={running || !picked.size}
                        onMouseEnter={() => setFolderHover(true)}
                        onMouseLeave={() => setFolderHover(false)}
                        title="One zip laid out like a billing period's folder: the billing summary, the internal review, the invoice package with the signed week summaries, the labor analysis and this workbook, plus the preflight record. While a condition below is unresolved it still downloads, named NOT READY, and is not recorded as a draft."
                        style={{
                            height: 36, padding: '0 16px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontFamily: 'inherit',
                            background: folderHover && !running && picked.size ? 'var(--muted)' : 'var(--background)', color: 'var(--foreground)', boxShadow: 'var(--shadow-xs)',
                            fontSize: 14, fontWeight: 500, cursor: running ? 'wait' : picked.size ? 'pointer' : 'not-allowed', opacity: picked.size ? 1 : 0.5,
                        }}
                    >
                        {running ? 'Building…' : 'Build invoice folder'}
                    </button>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {weeksSorted.length
                            ? `${weeksSorted.length} week${weeksSorted.length === 1 ? '' : 's'}: work ${moment(weekDates(weeksSorted[0])[0]).format('M/D/YY')} – ${moment(weeksSorted[weeksSorted.length - 1]).format('M/D/YY')}`
                            : 'Pick at least one week.'}
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)' }}>INVOICE FOLDER</span>
                    <Check label="Bill master exceptions on the card classification" checked={acceptMaster} onChange={setAcceptMaster} disabled={running}
                        title="A person the Attendance & Force Report master does not map, or whose classification is not on the rate sheet, bills at their JDE wage under the time card's classification (Seth 10/30/26). Each one is listed on the review and in the preflight record. A missing JDE wage still stops the build." />
                    <Check label="Allow a week summary still out for signature" checked={allowUnsigned} onChange={setAllowUnsigned} disabled={running}
                        title="The package carries the sent summary stamped AWAITING SIGNATURE instead of stopping." />
                </div>

                {Object.keys(steps).length ? (
                    <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                        {STEPS.map((s) => <StepRow key={s.key} label={s.label} step={steps[s.key]} />)}
                    </div>
                ) : null}
            </div>

            <div style={cardStyle}>
                <InvoiceDrafts
                    drafts={drafts}
                    invoiced={draftInvoiced}
                    userName={userName}
                    onReview={reviewOne}
                    onRebuild={rebuildDraft}
                    onRemove={removeDraft}
                    disabled={running}
                    canEdit={Boolean(saveRows) && !versionLocked}
                />
            </div>

            <div style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => setExOpen((o) => !o)} aria-expanded={exOpen} style={{ background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, color: 'var(--foreground)', cursor: 'pointer' }}>
                        {exOpen ? '▾' : '▸'} Invoice terms &amp; exceptions
                    </button>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {ex.terms.po ? `PO ${ex.terms.po}${ex.terms.poLine ? ` line ${ex.terms.poLine}` : ''}` : 'No PO on file'}
                        {Number(ex.terms.mobBalance) > 0 ? ` · mobilization credit ${money(Number(ex.terms.mobBalance))}` : ''}
                        {` · ${ex.classes.length} classification rule${ex.classes.length === 1 ? '' : 's'} · ${ex.people.length} accepted off the master · ${ex.units.length} unit rule${ex.units.length === 1 ? '' : 's'}`}
                    </span>
                    {exDraft ? <GhostButton label="Discard" onClick={() => setExDraft(null)} disabled={running} /> : null}
                    <button type="button" onClick={saveExceptions} disabled={!exDraft || running || versionLocked || !saveRows}
                        style={{ marginLeft: 'auto', height: 32, padding: '0 12px', borderRadius: 'var(--radius)', border: '1px solid transparent', fontFamily: 'inherit', background: 'var(--primary)', color: 'var(--primary-foreground)', fontSize: 14, fontWeight: 500, cursor: exDraft ? 'pointer' : 'not-allowed', opacity: exDraft ? 1 : 0.5 }}>
                        Save
                    </button>
                </div>
                {exOpen ? (
                    <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginBottom: 10, maxWidth: 820, lineHeight: 1.5 }}>
                            What the invoice scripts carried by hand each period. The terms print on the billing summary and labor analysis; a classification rule bills a card classification under a rate sheet line (or keeps its wording); a person accepted off the master bills on the card; a unit rule bills a card unit as a rate sheet item or leaves it off the invoice. Every rule the build uses is listed on the review and in the preflight record.
                        </div>
                        <ExGroup label="INVOICE TERMS">
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                {[['po', 'Purchase order', 110], ['poLine', 'PO line', 60], ['poLineName', 'PO line name', 180], ['afe', 'AFE', 100], ['terms', 'Terms', 80], ['so', 'Service Order (when there is no PO)', 220], ['mobBalance', 'Mobilization credit balance', 130], ['mobBasis', 'Credit basis wording', 260]].map(([k, label, w]) => (
                                    <ExField key={k} label={label}>
                                        <input value={String(ex.terms[k] ?? '')} onChange={(e) => editEx({ terms: { ...ex.terms, [k]: e.target.value } })} disabled={running || versionLocked} style={{ ...exInput, width: w }} placeholder={k === 'terms' ? EMPTY_TERMS.terms : k === 'mobBasis' ? EMPTY_TERMS.mobBasis : ''} />
                                    </ExField>
                                ))}
                            </div>
                        </ExGroup>
                        <ExGroup label="CLASSIFICATION RULES — a card classification and the rate sheet line it bills under">
                            {ex.classes.map((r, i) => (
                                <div key={r.rowId || `c${i}`} style={exRow}>
                                    <input value={r.card} onChange={(e) => editList('classes', i, { card: e.target.value })} disabled={running || versionLocked} placeholder="classification as the card spells it" style={{ ...exInput, width: 240 }} />
                                    <select value={r.as} onChange={(e) => editList('classes', i, { as: e.target.value })} disabled={running || versionLocked} style={{ ...exInput, width: 300 }}>
                                        <option value="">Keep the card&apos;s wording (bills at the JDE wage, no target)</option>
                                        {laborLines.map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                    <ExRemove onClick={() => removeFrom('classes', i)} disabled={running || versionLocked} />
                                </div>
                            ))}
                            <GhostButton label="Add classification rule" onClick={() => addTo('classes', { card: '', as: '' })} disabled={running || versionLocked} />
                        </ExGroup>
                        <ExGroup label="ACCEPTED OFF THE MASTER — people who bill on the card although the Attendance & Force Report master does not map them">
                            {ex.people.map((r, i) => (
                                <div key={r.rowId || `p${i}`} style={exRow}>
                                    <input value={r.id} onChange={(e) => editList('people', i, { id: e.target.value })} disabled={running || versionLocked} placeholder="Emp ID" style={{ ...exInput, width: 80 }} />
                                    <input value={r.name} onChange={(e) => editList('people', i, { name: e.target.value })} disabled={running || versionLocked} placeholder="name" style={{ ...exInput, width: 200 }} />
                                    <input value={r.note} onChange={(e) => editList('people', i, { note: e.target.value })} disabled={running || versionLocked} placeholder="why (who said so, when)" style={{ ...exInput, width: 300 }} />
                                    <ExRemove onClick={() => removeFrom('people', i)} disabled={running || versionLocked} />
                                </div>
                            ))}
                            <GhostButton label="Add person" onClick={() => addTo('people', { id: '', name: '', note: '' })} disabled={running || versionLocked} />
                        </ExGroup>
                        <ExGroup label="UNIT RULES — a card equipment description or unit number, billed as a rate sheet item or left off the invoice">
                            {ex.units.map((r, i) => (
                                <div key={r.rowId || `u${i}`} style={exRow}>
                                    <input value={r.match} onChange={(e) => editList('units', i, { match: e.target.value })} disabled={running || versionLocked} placeholder="card description or unit no" style={{ ...exInput, width: 280 }} />
                                    <select value={r.skip ? '' : (equipItems.find((n) => sameName(n, r.as)) || r.as)} onChange={(e) => editList('units', i, { as: e.target.value, skip: !e.target.value })} disabled={running || versionLocked} style={{ ...exInput, width: 300 }}>
                                        <option value="">Leave off the invoice</option>
                                        {equipItems.map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                    <ExRemove onClick={() => removeFrom('units', i)} disabled={running || versionLocked} />
                                </div>
                            ))}
                            <GhostButton label="Add unit rule" onClick={() => addTo('units', { match: '', as: '', skip: true })} disabled={running || versionLocked} />
                        </ExGroup>
                    </div>
                ) : null}
            </div>

            {folder ? (
                <div style={cardStyle}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--foreground)' }}>{folder.built ? 'Invoice folder' : 'Invoice folder not built'}</div>
                        {folder.built ? <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{folder.built.name}</div> : null}
                    </div>
                    {folder.checks.blocks.length ? (
                        <div style={{ padding: '10px 14px', borderRadius: 'var(--radius)', background: 'var(--destructive-tint)', border: '1px solid color-mix(in srgb, var(--destructive) 25%, transparent)' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--destructive-text)', marginBottom: 4 }}>
                                {folder.checks.blocks.length} condition{folder.checks.blocks.length === 1 ? '' : 's'} to settle before this folder is ready to invoice
                            </div>
                            {folder.checks.blocks.map((b) => <div key={b.text} style={{ fontSize: 13, color: 'var(--destructive-text)', lineHeight: 1.5 }}>• {b.text}</div>)}
                        </div>
                    ) : null}
                    {folder.built ? (
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                            {folder.built.files.map((f) => <div key={f} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{f}</div>)}
                            <div style={{ marginTop: 6 }}>Unzip it into the period&apos;s folder: the job&apos;s documents land in their own folder, the preflight and totals beside it. The package has {folder.built.packagePages} pages.</div>
                        </div>
                    ) : null}
                    {folder.checks.warnings.length ? (
                        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 'var(--radius)', background: 'var(--warning-tint)', border: '1px solid color-mix(in srgb, var(--warning) 25%, transparent)' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--warning-text)', marginBottom: 4 }}>Check before invoicing</div>
                            {folder.checks.warnings.map((w) => <div key={w} style={{ fontSize: 13, color: 'var(--warning-text)', lineHeight: 1.5 }}>• {w}</div>)}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {m ? (
                <div style={cardStyle}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--foreground)' }}>In the file</div>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{result.fileName}</div>
                        <div style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-secondary)' }}>
                            Labor + equipment <span style={{ fontSize: 17, fontWeight: 600, color: 'var(--foreground)', fontVariantNumeric: 'tabular-nums', marginLeft: 6 }}>{money(m.totals.total)}</span>
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                            <thead>
                                <tr>
                                    {['Week', 'Labor from', 'Equipment from', 'People', 'Labor hours', 'Labor', 'Per diem', 'Equipment', 'Fuel', 'Total'].map((h, i) => (
                                        <th key={h} style={{ ...th, ...(i >= 3 ? num : {}) }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {m.byWeek.map((w) => (
                                    <tr key={w.we}>
                                        <td style={td}>{weekLabel(w.we)}</td>
                                        <td style={td}>{w.laborBasis === 'card' ? <Badge>Time card</Badge> : w.laborBasis === 'hj' ? <Badge tone="warn">HeavyJob</Badge> : <Badge tone="bad">None</Badge>}</td>
                                        <td style={td}>{w.equipBasis === 'card' ? <Badge>Time card</Badge> : w.equipBasis === 'hj' ? <Badge tone="warn">HeavyJob</Badge> : <Badge>None</Badge>}</td>
                                        <td style={{ ...td, ...num }}>{w.people}</td>
                                        <td style={{ ...td, ...num }}>{hours(w.hrs)}</td>
                                        <td style={{ ...td, ...num }}>{money(w.labor)}</td>
                                        <td style={{ ...td, ...num }}>{money(w.pd)}</td>
                                        <td style={{ ...td, ...num }}>{money(w.equip)}</td>
                                        <td style={{ ...td, ...num }}>{money(w.fuel)}</td>
                                        <td style={{ ...td, ...num, fontWeight: 600 }}>{money(w.total)}</td>
                                    </tr>
                                ))}
                                <tr>
                                    <td style={{ ...td, fontWeight: 600, background: 'var(--subtle)' }} colSpan={4}>Total</td>
                                    <td style={{ ...td, ...num, fontWeight: 600, background: 'var(--subtle)' }}>{hours(m.totals.hrs)}</td>
                                    <td style={{ ...td, ...num, fontWeight: 600, background: 'var(--subtle)' }}>{money(m.totals.labor)}</td>
                                    <td style={{ ...td, ...num, fontWeight: 600, background: 'var(--subtle)' }}>{money(m.totals.pd)}</td>
                                    <td style={{ ...td, ...num, fontWeight: 600, background: 'var(--subtle)' }}>{money(m.totals.equip)}</td>
                                    <td style={{ ...td, ...num, fontWeight: 600, background: 'var(--subtle)' }}>{money(m.totals.fuel)}</td>
                                    <td style={{ ...td, ...num, fontWeight: 600, background: 'var(--subtle)' }}>{money(m.totals.total)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    {(result.cardTabs || []).length ? (
                        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
                            Time card tabs copied in: {(result.cardTabs || []).map((c) => `${weekLabel(c.we)} (${c.tabs.join(', ')})`).join('; ')}
                        </div>
                    ) : null}
                    {[...m.flags, ...(result.notes || [])].length ? (
                        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 'var(--radius)', background: 'var(--warning-tint)', border: '1px solid color-mix(in srgb, var(--warning) 25%, transparent)' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--warning-text)', marginBottom: 4 }}>Check before invoicing</div>
                            {[...m.flags, ...(result.notes || [])].map((f) => (
                                <div key={f} style={{ fontSize: 13, color: 'var(--warning-text)', lineHeight: 1.5 }}>• {f}</div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-secondary)' }}>Nothing flagged.</div>
                    )}
                </div>
            ) : null}
        </div>
    );
}
